import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, Thead, Tbody, Tr, Th, Td } from "@/components/ui/table";
import { useAuth } from "@/auth/AuthProvider";
import { isAdminRole } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";
import {
  isPendingRule,
  useExtractSopDocument,
  useSopDocuments,
  useSopRules,
  useUploadSopDocument,
  type SopDocument,
} from "@/api/queries/sop-documents";

/**
 * SOP Documents (RES-57/58) — chiefs upload their department's SOPs/SOGs,
 * trigger rule extraction, and jump into the review gate. The pipeline is:
 * upload (presigned PUT + server confirm) → extract (Bedrock, synchronous)
 * → review (/sop-documents/:id) → approved rules score the compliance report.
 */

export function SopDocumentsPage() {
  const { identity } = useAuth();
  if (!isAdminRole(identity?.role)) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">SOP Documents</h1>
        </header>
        <p className="text-sm text-destructive">
          SOP document management is restricted to chiefs and RespAI administrators.
        </p>
      </div>
    );
  }
  return <SopDocumentsPageInner />;
}

function SopDocumentsPageInner() {
  const documents = useSopDocuments();
  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">SOP Documents</h1>
          <Badge variant="warning">Preview</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Upload your department&apos;s SOPs/SOGs, extract checkable rules, then
          review each rule before it scores anything. No rule is active until a
          person approves it.
        </p>
      </header>

      <UploadSopCard />

      {documents.isLoading ? (
        <Skeleton className="h-48" />
      ) : documents.isError ? (
        <p className="text-sm text-destructive">
          Failed to load SOP documents
          {documents.error instanceof Error ? `: ${documents.error.message}` : ""}.
        </p>
      ) : documents.data && documents.data.length > 0 ? (
        <SopDocumentsTable documents={documents.data} />
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              No SOP documents yet. Upload your department&apos;s SOG/SOP PDF to
              start the extract-and-review pipeline.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ───────────────────────────── Upload card ──────────────────────────────── */

type UploadPhase = "idle" | "uploading";

function UploadSopCard() {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadSopDocument();
  const [phase, setPhase] = useState<UploadPhase>("idle");

  const onFile = (file: File | undefined) => {
    if (!file) return;
    setPhase("uploading");
    upload.mutate(file, { onSettled: () => setPhase("idle") });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload an SOP/SOG document</CardTitle>
        <p className="text-sm text-muted-foreground">
          PDF, Word, or plain text, up to 25 MB. Re-uploading the same filename
          creates a new version.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
          className="hidden"
          data-testid="sop-file-input"
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <Button
          disabled={phase !== "idle"}
          onClick={() => inputRef.current?.click()}
        >
          {phase === "uploading" ? "Uploading…" : "Choose file to upload"}
        </Button>
        {upload.isError ? (
          <p className="text-sm text-destructive">
            {upload.error instanceof Error
              ? upload.error.message
              : "Upload failed."}
          </p>
        ) : null}
        {upload.isSuccess && upload.data ? (
          <p className="text-sm text-muted-foreground">
            Uploaded {upload.data.filename} (v{upload.data.version}). Extract
            rules below when ready.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────────── Documents table ────────────────────────────── */

export function DocumentStatusBadge({ status }: { status: string }) {
  switch (status) {
    case "pending_upload":
      return <Badge variant="outline">Awaiting upload</Badge>;
    case "uploaded":
      return <Badge variant="secondary">Uploaded</Badge>;
    case "extracting":
      return <Badge variant="warning">Extracting…</Badge>;
    case "extracted":
      return <Badge variant="success">Rules extracted</Badge>;
    case "failed":
      return <Badge variant="destructive">Failed</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function fmtSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1000))} KB`;
}

function SopDocumentsTable({ documents }: { documents: SopDocument[] }) {
  const rules = useSopRules();
  const pendingByDoc = new Map<string, number>();
  const approvedByDoc = new Map<string, number>();
  for (const rule of rules.data ?? []) {
    if (isPendingRule(rule)) {
      pendingByDoc.set(
        rule.sop_document_id,
        (pendingByDoc.get(rule.sop_document_id) ?? 0) + 1,
      );
    } else if (rule.status === "approved") {
      approvedByDoc.set(
        rule.sop_document_id,
        (approvedByDoc.get(rule.sop_document_id) ?? 0) + 1,
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <Thead>
              <Tr>
                <Th>Document</Th>
                <Th>Uploaded at</Th>
                <Th>Size</Th>
                <Th>Status</Th>
                <Th>Rules</Th>
                <Th className="text-right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {documents.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  pendingCount={pendingByDoc.get(doc.id) ?? 0}
                  approvedCount={approvedByDoc.get(doc.id) ?? 0}
                />
              ))}
            </Tbody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentRow({
  doc,
  pendingCount,
  approvedCount,
}: {
  doc: SopDocument;
  pendingCount: number;
  approvedCount: number;
}) {
  const extract = useExtractSopDocument();
  // Backend allows (re-)extraction from uploaded / extracted / failed.
  const extractable = ["uploaded", "extracted", "failed"].includes(doc.status);
  const hasRules = pendingCount + approvedCount > 0;

  return (
    <Tr>
      <Td>
        <div className="font-medium">{doc.filename}</div>
        <div className="text-xs text-muted-foreground">v{doc.version}</div>
        {doc.status === "failed" && doc.error_message ? (
          <div className="mt-1 max-w-xs text-xs text-destructive">
            {doc.error_message}
          </div>
        ) : null}
        {extract.isError ? (
          <div className="mt-1 max-w-xs text-xs text-destructive">
            {extract.error instanceof Error
              ? extract.error.message
              : "Extraction failed."}
          </div>
        ) : null}
      </Td>
      <Td className="whitespace-nowrap text-muted-foreground">
        {formatDateTime(doc.created_at)}
      </Td>
      <Td className="whitespace-nowrap text-muted-foreground">
        {fmtSize(doc.size_bytes)}
      </Td>
      <Td>
        <DocumentStatusBadge status={extract.isPending ? "extracting" : doc.status} />
      </Td>
      <Td className="whitespace-nowrap text-muted-foreground">
        {hasRules ? `${pendingCount} pending · ${approvedCount} approved` : "—"}
      </Td>
      <Td className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!extractable || extract.isPending}
            onClick={() => extract.mutate(doc.id)}
          >
            {extract.isPending
              ? "Extracting…"
              : doc.status === "extracted"
                ? "Re-extract"
                : "Extract rules"}
          </Button>
          {doc.status === "extracted" || hasRules ? (
            <Link
              to={`/sop-documents/${doc.id}`}
              className="text-sm text-primary hover:underline"
            >
              Review rules →
            </Link>
          ) : (
            <span className="text-sm text-muted-foreground/50">Review rules →</span>
          )}
        </div>
      </Td>
    </Tr>
  );
}
